<script setup lang="ts">
/*
 * Компонент DateTimeMaskInput — единое поле ввода даты и времени для форм проекта.
 *
 * Возможности:
 * - Ручной ввод по маске «##.##.#### ##:##» через VMaskInput из vuetify/labs.
 * - Прогрессивный плейсхолдер «дд.мм.гггг чч:мм»: оставшаяся часть остаётся
 *   видна на сером фоне по мере заполнения.
 * - Фокус в поле или клик по иконке календаря открывает popup DateTimePicker.vue.
 * - Двусторонняя синхронизация: ввод в маске обновляет календарь; Save в календаре
 *   пишет значение в поле (через ISO v-model и watcher маски).
 * - Семантическая ошибка («32.13.2026 14:00» → «Некорректная дата или время»)
 *   объединяется с внешними errorMessages.
 *
 * Контракт:
 * - v-model: ISO-строка (string | null).
 * - props.error / props.errorMessages — внешние ошибки от родителя.
 * - props.onlyPastDate / props.onlyFutureDate — пробрасываются в DateTimePicker.
 */

import { computed, ref, watch } from 'vue'
import { VMaskInput } from 'vuetify/labs/VMaskInput'

import DateTimePicker from '@/components/createdUi/DateTimePicker.vue'

interface Props {
  modelValue: string | null
  error?: boolean
  errorMessages?: string | string[]
  disabled?: boolean
  onlyPastDate?: boolean
  onlyFutureDate?: boolean
  density?: 'default' | 'comfortable' | 'compact'
}

interface Emits {
  (e: 'update:modelValue', value: string | null): void
  (e: 'out-of-range', reason: 'past' | 'future'): void
}

const props = withDefaults(defineProps<Props>(), {
  error: false,
  errorMessages: () => [],
  disabled: false,
  onlyPastDate: false,
  onlyFutureDate: false,
  density: 'comfortable',
})

const emit = defineEmits<Emits>()

/**
 * ISO-строка → формат маски «дд.мм.гггг чч:мм».
 */
const isoToMasked = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Парсит строку формата «дд.мм.гггг чч:мм» с проверкой диапазонов
 * (день 32, месяц 13 и т.п.). null если строка не полностью заполнена
 * или значения вне допустимых диапазонов.
 */
const parseMasked = (value: string): Date | null => {
  const match = value.match(/^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})$/)
  if (!match) return null
  const [, dStr, mStr, yStr, hStr, minStr] = match
  const day = Number(dStr)
  const month = Number(mStr)
  const year = Number(yStr)
  const hour = Number(hStr)
  const minute = Number(minStr)
  const d = new Date(year, month - 1, day, hour, minute)
  if (
    d.getDate() !== day
    || d.getMonth() !== month - 1
    || d.getFullYear() !== year
    || d.getHours() !== hour
    || d.getMinutes() !== minute
  ) return null

  return d
}

/**
 * Локальное состояние поля. VMaskInput даёт структурный контроль (только цифры
 * на местах #), семантический даёт parseMasked + watcher.
 */
const maskedInput = ref<string>(isoToMasked(props.modelValue))

/**
 * Ввод пользователя → ISO в модели. Любой неполный/невалидный ввод
 * (включая частично стёртое значение) эмитит null — родитель видит, что
 * deadline сброшен и блокирует submit-кнопки. Полное и валидное значение
 * эмитится как ISO. Защита от ложного эмита, если разница лишь в секундах/мс
 * (маска хранит только минуты).
 */
watch(maskedInput, value => {
  if (!value) {
    if (props.modelValue !== null) emit('update:modelValue', null)

    return
  }

  const parsed = parseMasked(value)
  if (!parsed) {
    // Частичный или невалидный ввод (например пользователь стёр минуты,
    // оставив «01.01.2027 12:») — сбрасываем модель, чтобы submit-кнопка,
    // зависящая от modelValue, перестала быть активной.
    if (props.modelValue !== null) emit('update:modelValue', null)

    return
  }

  /* Структурно валидная дата вне onlyPastDate/onlyFutureDate: принудительно очищаем
     маску (через прямое присваивание, чтобы не зависеть от watch'а modelValue —
     к этому моменту modelValue мог уже быть null из-за частичного ввода ранее)
     и эмитим 'out-of-range' — родитель показывает тост с причиной. */
  if (props.onlyPastDate && parsed.getTime() > Date.now()) {
    maskedInput.value = ''
    if (props.modelValue !== null) emit('update:modelValue', null)
    emit('out-of-range', 'past')

    return
  }
  if (props.onlyFutureDate && parsed.getTime() < Date.now()) {
    maskedInput.value = ''
    if (props.modelValue !== null) emit('update:modelValue', null)
    emit('out-of-range', 'future')

    return
  }

  if (props.modelValue) {
    const existing = new Date(props.modelValue)
    if (
      existing.getFullYear() === parsed.getFullYear()
      && existing.getMonth() === parsed.getMonth()
      && existing.getDate() === parsed.getDate()
      && existing.getHours() === parsed.getHours()
      && existing.getMinutes() === parsed.getMinutes()
    ) return
  }

  emit('update:modelValue', parsed.toISOString())
})

/**
 * Внешнее изменение modelValue (загрузка из бэка, выбор в picker'е) → маска.
 * Защита: при частичном вводе наш же watcher эмитит null, но это не должно
 * затирать набранную пользователем строку. Если modelValue стал null,
 * а в поле частичная маска — оставляем как есть.
 */
const FULL_MASK_RE = /^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/

watch(() => props.modelValue, iso => {
  const masked = isoToMasked(iso)
  if (masked === maskedInput.value) return
  if (iso === null && maskedInput.value && !FULL_MASK_RE.test(maskedInput.value)) return
  maskedInput.value = masked
})

/**
 * Семантическая ошибка показывается только когда маска полностью заполнена,
 * но компоненты невалидны. Частичный ввод не помечается ошибкой.
 */
const semanticError = computed<string>(() => {
  if (!maskedInput.value) return ''
  const isFullyFilled = /^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/.test(maskedInput.value)
  if (!isFullyFilled) return ''

  return parseMasked(maskedInput.value) ? '' : 'Некорректная дата или время'
})

/**
 * Объединяет внешние errorMessages родителя и внутреннюю semanticError
 * в одном массиве для VMaskInput.
 */
const combinedErrorMessages = computed<string[]>(() => {
  const out: string[] = []
  const ext = props.errorMessages
  if (ext) {
    if (Array.isArray(ext)) out.push(...ext.filter(Boolean))
    else out.push(ext)
  }
  if (semanticError.value && !out.includes(semanticError.value)) {
    out.push(semanticError.value)
  }

  return out
})

const isPickerOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)

/**
 * Частичный разбор маски для live-синхронизации с календарём.
 * Нужна хотя бы полная дата DD.MM.YYYY; часы/минуты — по мере ввода двух цифр,
 * иначе берём из modelValue или «сейчас». Невалидная дата → null (календарь
 * остаётся на предыдущем значении: watcher DateTimePicker игнорирует null).
 */
const parsePartialForPicker = (value: string): Date | null => {
  const match = value.match(/^(\d{2})\.(\d{2})\.(\d{4})(?: (\d{1,2})(?::(\d{1,2}))?)?/)
  if (!match) return null

  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  const hourPart = match[4]
  const minutePart = match[5]

  const base = props.modelValue
    ? new Date(props.modelValue)
    : new Date()
  const baseOk = !Number.isNaN(base.getTime())
  let hour = baseOk ? base.getHours() : new Date().getHours()
  let minute = baseOk ? base.getMinutes() : new Date().getMinutes()

  if (hourPart?.length === 2) {
    hour = Number(hourPart)
    if (hour > 23) return null
  }
  if (minutePart?.length === 2) {
    minute = Number(minutePart)
    if (minute > 59) return null
  }

  const d = new Date(year, month - 1, day, hour, minute, 0, 0)
  if (
    d.getDate() !== day
    || d.getMonth() !== month - 1
    || d.getFullYear() !== year
  ) return null

  return d
}

/**
 * Значение для DateTimePicker: сначала из маски (в т.ч. частичной),
 * иначе из ISO modelValue (после выбора в календаре / загрузки с бэка).
 */
const pickerDate = computed<Date | null>(() => {
  const fromMask = parsePartialForPicker(maskedInput.value)
  if (fromMask) return fromMask
  if (!props.modelValue) return null
  const d = new Date(props.modelValue)

  return Number.isNaN(d.getTime()) ? null : d
})

/**
 * Сохранение выбранного значения из picker'а: закрываем popup и эмитим ISO
 * (либо null, если очистили). Watcher на modelValue подтянет значение в маску.
 */
const onPickerSave = (date: Date | null) => {
  isPickerOpen.value = false
  if (!date) {
    emit('update:modelValue', null)

    return
  }
  emit('update:modelValue', date.toISOString())
}

let justFocused = false

/** Открыть календарь при фокусе в поле. */
const onFieldFocus = () => {
  if (props.disabled) return
  isPickerOpen.value = true
  justFocused = true
  setTimeout(() => {
    justFocused = false
  }, 200)
}

/**
 * Toggle popup'а picker'а по клику на иконку календаря.
 * В disabled-состоянии не открываем.
 */
const onCalendarClick = () => {
  if (props.disabled) return
  if (justFocused) {
    isPickerOpen.value = true

    return
  }
  isPickerOpen.value = !isPickerOpen.value
}

const PLACEHOLDER = 'дд.мм.гггг чч:мм'

/**
 * Прогрессивный плейсхолдер: введённая часть прозрачна, оставшаяся —
 * серая. Так пользователь видит «куда вводить» по мере заполнения.
 */
const placeholderParts = computed(() => {
  const filledLength = maskedInput.value.length

  return {
    invisible: PLACEHOLDER.slice(0, filledLength),
    visible: PLACEHOLDER.slice(filledLength),
  }
})
</script>

<template>
  <div ref="rootRef" class="datetime-mask-input">
    <VMaskInput
      v-model="maskedInput"
      :error="error"
      :error-messages="combinedErrorMessages"
      :disabled="disabled"
      :density="density"
      mask="##.##.#### ##:##"
      return-masked-value
      placeholder=""
      variant="outlined"
      hide-details="auto"
      append-inner-icon="ri-calendar-line"
      persistent-placeholder
      @focus="onFieldFocus"
      @click:append-inner="onCalendarClick"
    >
      <template v-if="$slots.label" #label>
        <slot name="label" />
      </template>
    </VMaskInput>

    <!--
      Прогрессивный плейсхолдер: отрисовывается поверх input'а.
      Введённая часть прозрачна (placement-only), оставшаяся — серая.
    -->
    <span class="datetime-mask-input__placeholder" aria-hidden="true">
      <span class="datetime-mask-input__placeholder-filled">{{ placeholderParts.invisible }}</span>
      <span class="datetime-mask-input__placeholder-rest">{{ placeholderParts.visible }}</span>
    </span>

    <!--
      VMenu без activator: позиционируется по rootRef через :target,
      click listener не вешается — управление полностью через v-model
      и onCalendarClick. Иначе activator="parent" перекрывал бы наш
      toggle и попап мерцал.
    -->
    <VMenu
      v-model="isPickerOpen"
      :close-on-content-click="false"
      :target="rootRef ?? undefined"
      :disabled="disabled"
      offset="8"
      content-class="datetime-picker-menu"
    >
      <VCard>
        <DateTimePicker
          :model-value="pickerDate"
          :only-past-date="onlyPastDate"
          :only-future-date="onlyFutureDate"
          @save="onPickerSave"
        />
      </VCard>
    </VMenu>
  </div>
</template>

<style scoped lang="scss">
.datetime-mask-input {
  position: relative;
}

.datetime-mask-input__placeholder {
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  inline-size: 100%;
  block-size: 100%;
  padding-inline: 16px;
  display: flex;
  align-items: center;
  pointer-events: none;
  font-size: 0.9375rem;
  line-height: 1.5;
  white-space: pre;
}

.datetime-mask-input__placeholder-filled {
  color: transparent;
}

.datetime-mask-input__placeholder-rest {
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
  padding-inline-start: 1ch;
}
</style>
